
import { FormDraft, FormDraftMetadata } from '../types/formDraft';
import { TrainingFormValues } from '@/types/training';
import { STORAGE_KEYS } from '@/types/training';

export const saveDraft = (
  values: Partial<TrainingFormValues>, 
  metadata: Partial<FormDraftMetadata>
): void => {
  try {
    // Ensure date is always a Date object for the draft object
    const processedDate = values.date instanceof Date 
      ? values.date 
      : values.date 
        ? new Date(values.date) 
        : new Date();

    const draft: FormDraft = {
      metadata: {
        isEditing: metadata.isEditing || false,
        sessionId: metadata.sessionId,
        lastUpdated: new Date().toISOString(),
      },
      values: {
        ...values,
        date: processedDate,
        // Ensure all text fields are defined with empty string fallbacks
        professor: values.professor || '',
        techniques: values.techniques || '',
        location: values.location || '',
        notes: values.notes || '',
        learned: values.learned || '',
        improvements: values.improvements || '',
        successes: values.successes || '',
        youtube_url: values.youtube_url || '',
        belt_rank: values.belt_rank || 'white',
        stripe_count: values.stripe_count || 'no stripes',
        competition_finish: values.competition_finish || '',
        competition_weight_classes: values.competition_weight_classes || '',
      },
    };
    
    // Deep clone and convert all values to JSON-friendly format for localStorage
    const storableDraft = JSON.parse(JSON.stringify({
      metadata: draft.metadata,
      values: {
        ...draft.values,
        date: processedDate.toISOString(),
        // Explicitly include all text fields to ensure they're saved with proper defaults
        professor: draft.values.professor || '',
        techniques: draft.values.techniques || '',
        location: draft.values.location || '',
        notes: draft.values.notes || '',
        learned: draft.values.learned || '',
        improvements: draft.values.improvements || '',
        successes: draft.values.successes || '',
        youtube_url: draft.values.youtube_url || '',
        belt_rank: draft.values.belt_rank || 'white',
        stripe_count: draft.values.stripe_count || 'no stripes',
        competition_finish: draft.values.competition_finish || '',
        competition_weight_classes: draft.values.competition_weight_classes || '',
        competition_total_matches: draft.values.competition_total_matches || 0,
        competition_total_wins: draft.values.competition_total_wins || 0,
        competition_matches: draft.values.competition_matches || []
      }
    }));
    
    localStorage.setItem(STORAGE_KEYS.FORM_DRAFT, JSON.stringify(storableDraft));

  } catch (error) {
    console.error('Error saving form draft:', error);
  }
};

export const loadDraft = (): FormDraft | null => {
  try {
    const draftJson = localStorage.getItem(STORAGE_KEYS.FORM_DRAFT);
    if (!draftJson) return null;
    
    const parsedDraft = JSON.parse(draftJson);
    
    // Convert ISO date string back to Date object if present
    if (parsedDraft.values.date) {
      parsedDraft.values.date = new Date(parsedDraft.values.date);
    }
    
    // Ensure all text fields are loaded with proper defaults
    const draftValues: Partial<TrainingFormValues> = {
      ...parsedDraft.values,
      professor: parsedDraft.values.professor || '',
      techniques: parsedDraft.values.techniques || '',
      location: parsedDraft.values.location || '',
      notes: parsedDraft.values.notes || '',
      learned: parsedDraft.values.learned || '',
      improvements: parsedDraft.values.improvements || '',
      successes: parsedDraft.values.successes || '',
      youtube_url: parsedDraft.values.youtube_url || '',
      belt_rank: parsedDraft.values.belt_rank || 'white',
      stripe_count: parsedDraft.values.stripe_count || 'no stripes',
      competition_finish: parsedDraft.values.competition_finish || '',
      competition_weight_classes: parsedDraft.values.competition_weight_classes || '',
      competition_total_matches: parsedDraft.values.competition_total_matches || 0,
      competition_total_wins: parsedDraft.values.competition_total_wins || 0,
      competition_matches: parsedDraft.values.competition_matches || []
    };
    
    const fullDraft: FormDraft = {
      metadata: parsedDraft.metadata,
      values: draftValues
    };
    
    return fullDraft;
  } catch (error) {
    console.error('Error loading form draft:', error);
    return null;
  }
};

export const clearDraft = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.FORM_DRAFT);
  } catch (error) {
    console.error('Error clearing form draft:', error);
  }
};

export const hasDraft = (): boolean => {
  return !!localStorage.getItem(STORAGE_KEYS.FORM_DRAFT);
};
